import _result from 'lodash/result';
import _find from 'lodash/find';
import _values from 'lodash/values';
import _has from 'lodash/has';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

// Actions
import { fetchTopicTree } from '../actions/action-message-topic-tree';
import { selectTopicL1, selectTopicL2, selectTopicL3, selectTopicReset, selectTopic } from '../actions/action-select-topic';

// Components
import TopicBreadCrumb from '../components/TopicBreadCrumb';
import TopicList from '../components/TopicList';

// Helper
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { trackLink } from '../../lib/analytics-helper';
import * as shape from '../constants/PropTypesShapes';

const heading = i18n.message('smarthelp/channelSelection.topicSelections.browser.header');
const isMobileLayout = Page.isMobileLayout();
const contactHeader = i18n.message('smarthelp/channelSelection.contactUs.header');

class TopicTreeBrowser extends Component {
  // Find subtopics by each layer topic name
  static getSubtopics(topics, layerName = []) {
    if (layerName.length === 0) {
      return topics;
    }
    const layer = layerName.shift();
    return TopicTreeBrowser.getSubtopics(_result(_find(topics, obj => (
      obj.topic_name === layer
    )), 'sub_topics'), layerName);
  }

  // check Object has value or not
  static hasValue(obj) {
    if (obj) {
      const values = _values(obj);
      return values.some(value => value !== '');
    }
    return false;
  }

  constructor(props) {
    super(props);

    /**
     * subTopic: store current display topic list
     * layer: store current topic data layer
     * topics: store topic name for bread crumb
     */
    this.state = {
      layer: 1,
      topics: {
        layer1: '',
        layer2: '',
        layer3: '',
      },
      topicRef: {
        layer1: '',
        layer2: '',
        layer3: '',
      },
      subTopics: [],
    };

    // Binding this for actions
    this.clickTopicItem = this.clickTopicItem.bind(this);
    this.clickBreadCrumb = this.clickBreadCrumb.bind(this);
  }

  componentDidMount() {
    this.props.fetchTopicTree();
  }

  // To get subTopics for TopicList
  getTopicsList() {
    if (this.state.subTopics && this.state.subTopics.length !== 0) {
      return this.state.subTopics;
    } else if (TopicTreeBrowser.hasValue(this.props.topicSelect.topics)) {
      // This condition is for data persist
      return this.props.topicSelect.subTopics;
    }
    return this.props.topicTree.topicTreeSelection.sub_topics;
  }

  // To get state for Topic data persist
  getSelectedState(key) {
    let state = {};
    if (TopicTreeBrowser.hasValue(this.state.topics)) {
      state = this.state;
    } else {
      // This condition is for data persist
      state = this.props.topicSelect;
    }

    if (key && _has(state, key)) {
      return state[key];
    }
    return state;
  }

  // Set Topic tree layer for Click event
  setTopicLayer(item) {
    const key = `layer${item.layer}`;
    const topics = this.getSelectedState('topics');
    topics[key] = item.content.topic_name;

    const topicRef = this.getSelectedState('topicRef');
    topicRef[key] = item.content.ref_id;

    this.setState({ topics, topicRef });
  }

  // breadcrumb switch layer logic
  switchLayerLogic(layer) {
    if (layer) {
      const layerNumb = parseInt(layer[layer.length - 1], 10);
      const layerName = [];
      const topics = {
        layer1: '',
        layer2: '',
        layer3: '',
      };
      const topicRef = {
        layer1: '',
        layer2: '',
        layer3: '',
      };
      const stateTopic = this.getSelectedState('topics');
      const stateTopicRef = this.getSelectedState('topicRef');
      // Set topics data with layer and state info
      for (let i = 1; i < layerNumb; i += 1) {
        const key = `layer${i}`;
        topics[key] = stateTopic[key];
        layerName.push(stateTopic[key]);
        topicRef[key] = stateTopicRef[key];
      }

      // Track FPTI for breadcrumb
      trackLink({ id: 'contactUsPage', link: `smarthelp-topics-breadcrumb|${layer}|${stateTopicRef[layer]}` });

      const breadStatus = {
        subTopics: TopicTreeBrowser.getSubtopics(this.props.topicTree.topicTreeSelection.sub_topics, layerName),
        topics,
        topicRef,
        layer: layerNumb,
      };

      this.setState(breadStatus);

      // Update global state for data persist
      this.props.selectTopic(breadStatus);
    }
  }

  // Topic List switch layer logic for select topic
  switchSelectTopic(layer, options) {
    switch (layer) {
      case 1:
        this.props.selectTopicL1(options);
        break;
      case 2:
        this.props.selectTopicL2(options);
        break;
      case 3:
        this.props.selectTopicL3(options);
        break;
      default:
        break;
    }
  }

  // Handle Breadcrumb Click event
  clickBreadCrumb(item) {
    if (item.item && item.item.length > 0) {
      const layer = item.item[0];
      // User Layer info to go to previous selection
      this.switchLayerLogic(layer);
      this.props.selectTopicReset();
    }
  }

  // Handle Topic Tree Browser Click event
  clickTopicItem(item) {
    // Set the topic info for select topic
    this.setTopicLayer(item);

    // Check topic tree reaching to the end
    if (item.content.sub_topics && item.content.sub_topics.length > 0) {
      // Not reach the end of topic tree
      const layer = this.getSelectedState('layer') + 1;
      this.selectTopics(this.getSelectedState('layer'), item, item.content);

      const topicStatus = {
        layer,
        subTopics: item.content.sub_topics,
      };
      this.setState(topicStatus);

      // Set global state for data persist
      topicStatus.topics = this.getSelectedState('topics');
      this.props.selectTopic(topicStatus);
    } else {
      // Reach the end of topic tree selection
      this.selectTopics(this.getSelectedState('layer'), item, null);
      this.props.selectTopic(this.getSelectedState());
    }
  }

  // To handle topic Select, update state and fire request
  selectTopics(layer, item, subTopics) {
    const options = {
      topicId: item.content.topic_id,
      topicName: item.content.topic_name,
      topicRefId: item.content.ref_id,
    };

    if (subTopics) {
      options.subTopic = subTopics;
    } else {
      options.content = item.content;
    }

    this.switchSelectTopic(layer, options);
  }

  // Topic Label filter which is only for the first layer in the desktop
  topicLabelsFilter() {
    const nonTopics = _values(this.getSelectedState('topics')).every(topic => topic === '');
    const firstLayer = this.getSelectedState('layer') === 1;

    if (!isMobileLayout && nonTopics && firstLayer) {
      return this.props.topicLabels;
    }
    return {};
  }


  // Render BreadCrumb above topic list
  renderBreadCrumb() {
    return (
      <TopicBreadCrumb
        items={this.getSelectedState('topics')}
        onClick={this.clickBreadCrumb}
        mobile={isMobileLayout}
      />
    );
  }

  // Render topic list with topics selection
  renderTopicList() {
    if (!this.props.topicsDisplay) {
      return null;
    }
    return (
      <TopicList
        topicList={this.getTopicsList()}
        onClick={this.clickTopicItem}
        layer={this.getSelectedState('layer')}
        labels={this.topicLabelsFilter()}
      />
    );
  }

  renderTopicContent() {
    // Null Check for Topic tree data
    if (!this.props.topicTree.topicTreeSelection) {
      return null;
    }

    return (
      <div className="col-xs-12 col-sm-10 col-md-10 col-lg-10 ">
        <h1 className="topic-tree-browser-header">{heading}</h1>
        {this.renderBreadCrumb()}
        {this.renderTopicList()}
      </div>
    );
  }

  // Render Topic Tree Container with Bootstrap Grid System
  renderTopicContainer() {
    return (
      <div className="topic-tree-browser-container">
        <h1 className="contact-us-header">{contactHeader}</h1>
        <div className="col-xs-0 col-sm-1 col-md-1 col-lg-1" />
        {this.renderTopicContent()}
        <div className="col-xs-0 col-sm-1 col-md-1 col-lg-1" />
      </div>
    );
  }

  render() {
    if (!this.props.display) {
      return null;
    }
    return this.renderTopicContainer();
  }
}

TopicTreeBrowser.propTypes = {
  fetchTopicTree: PropTypes.func.isRequired,
  selectTopicL1: PropTypes.func.isRequired,
  selectTopicL2: PropTypes.func.isRequired,
  selectTopicL3: PropTypes.func.isRequired,
  selectTopicReset: PropTypes.func.isRequired,
  selectTopic: PropTypes.func.isRequired,
  topicTree: PropTypes.shape(shape.TOPICTREE).isRequired,
  display: PropTypes.bool.isRequired,
  topicsDisplay: PropTypes.bool.isRequired,
  topicSelect: PropTypes.shape(shape.TOPICSELECT).isRequired,
  topicLabels: PropTypes.object,  // eslint-disable-line react/forbid-prop-types
};

TopicTreeBrowser.defaultProps = {
  topicLabels: {},
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ fetchTopicTree, selectTopicL1, selectTopicL2, selectTopicL3, selectTopicReset, selectTopic }, dispatch);
}

function mapStateToProps(state) {
  return {
    topicSelect: state.topicSelect,
    topicTree: state.topicTree,
    display: state.topicTree.display,
    topicsDisplay: state.selectResult.topicsDisplay,
  };
}


export default connect(mapStateToProps, mapDispatchToProps)(TopicTreeBrowser);
